import { OPS_COLORS } from '@/components/OpsOnDutyBar/OpsOnDutyBar.styles';
import styled from '@/styles';

// Admin › Operations (GRAVY-10878). Every value is from the UI spec (§5-§10,
// §15), measured off the approved prototype.
export const C = {
  ...OPS_COLORS,
  lightGreen: 'rgb(231,244,230)',
  hoverGreen: 'rgb(246,251,245)',
  softBorder: 'rgb(234,234,234)',
  hoverGrey: 'rgb(242,242,242)',
  weekdayBg: 'rgba(234,234,234,0.5)',
  amber: 'rgb(232,163,23)',
} as const;

const FONT =
  'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const focusRing = {
  outline: `2px solid ${C.darkGreen}`,
  outlineOffset: '2px',
};

const buttonReset = {
  margin: 0,
  padding: 0,
  border: 'none',
  // Bare text and icon buttons (Retry, Reload, Edit, Manage managers,
  // the chevrons, the X) get a shape, so the focus ring follows it. Filled
  // buttons set their own radius.
  borderRadius: '6px',
  background: 'none',
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
  '&:focus-visible': focusRing,
};

/** The page title: Manrope 20/800 ink on a 28px row, not Page's. */
export const PageTitle = styled('h2', {
  margin: 0,
  fontFamily: FONT,
  fontSize: '20px',
  fontWeight: 800,
  lineHeight: '28px',
  color: C.ink,
});

// Page's content top margin is off (`contentVarient`): 24px from the title
// row to the preview card, as in the prototype.
export const Root = styled('div', {
  marginTop: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  color: C.ink,
  fontFamily: FONT,
  fontSize: '16px',
  lineHeight: 1.4,
  '& *': { boxSizing: 'border-box' },
});

// ---- cards ----------------------------------------------------------------

export const Card = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  padding: '24px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  '@phoneOnly': { padding: '16px' },
});

export const CardHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px 16px',
  minHeight: '28px',
});

export const CardTitle = styled('h2', {
  margin: 0,
  fontSize: '20px',
  fontWeight: 800,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
});

export const HeaderAside = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px 16px',
});

export const ZoneNote = styled('p', {
  margin: 0,
  fontSize: '14px',
  color: C.muted,
});

export const LinkButton = styled('button', {
  ...buttonReset,
  fontSize: '14px',
  fontWeight: 600,
  color: C.darkGreen,
  '&:hover': { color: C.green, textDecoration: 'underline' },
});

export const ErrorBox = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgb(255,242,242)',
  boxShadow: `inset 0 0 0 1px rgb(245,200,200)`,
  color: C.red,
  fontSize: '14px',
  fontWeight: 600,
});

/** Inline server errors in a form: one line per message. */
export const ErrorPanel = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgb(255,242,242)',
  boxShadow: 'inset 0 0 0 1px rgb(245,200,200)',
  color: C.red,
  fontSize: '14px',
  fontWeight: 600,
  '& ul': { margin: 0, padding: 0, listStyle: 'none' },
  '& li + li': { marginTop: '4px' },
});

export const Muted = styled('p', {
  margin: 0,
  fontSize: '16px',
  color: C.muted,
});

// ---- buttons --------------------------------------------------------------

export const IconButton = styled('button', {
  ...buttonReset,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: C.ink,
  '&:disabled': { cursor: 'default', opacity: 0.4 },
  variants: {
    size: {
      stepper: { width: '36px', height: '36px', '& svg': { width: '20px' } },
      month: { width: '24px', height: '24px', '& svg': { width: '24px' } },
    },
  },
});

/** "Add another schedule" (UI spec §9.5). */
export const SecondaryButton = styled('button', {
  ...buttonReset,
  height: '36px',
  padding: '8px 16px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: `inset 0 0 0 1px ${C.green}`,
  color: C.darkGreen,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': { background: C.hoverGreen },
  // Add another is disabled with Save while the form is invalid.
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

/** "Save schedule" / "Save changes" / "Apply selection" (UI spec §9.3, §9.5). */
export const PrimaryButton = styled('button', {
  ...buttonReset,
  height: '36px',
  padding: '8px 16px',
  borderRadius: '12px',
  background: C.green,
  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.12)',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': { background: C.darkGreen },
  // Invalid → opacity 0.4, not-allowed.
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

/** Text-only "Cancel" (UI spec §9.5). */
export const TextButton = styled('button', {
  ...buttonReset,
  height: '36px',
  padding: '8px 16px',
  borderRadius: '12px',
  color: C.darkGreen,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': { background: C.hoverGreen },
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

/** The delete confirm's filled red button, on the Primary shape. */
export const DangerButton = styled(PrimaryButton, {
  background: 'rgb(252,60,60)',
  '&:hover:not(:disabled)': { background: 'rgb(222,38,38)' },
});

// ---- calendar (§7) ----------------------------------------------------------

// The prototype's split: calendar `1 1 480px` up to 600px (the Repeat
// modal's calendar too), panel `1 1 340px`, 16 gap. See Body for the page.
export const CalendarRoot = styled('div', {
  flex: '1 1 480px',
  maxWidth: '600px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  transition: 'opacity .15s ease',
  variants: {
    // Edit mode dims the calendar and stops clicks.
    dimmed: { true: { opacity: 0.4, pointerEvents: 'none' } },
  },
});

export const MonthHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '49px',
  padding: '12px 8px',
});

export const MonthTitle = styled('h3', {
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

export const WeekdayRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  height: '33px',
  padding: '8px',
  background: C.weekdayBg,
});

export const Weekday = styled('abbr', {
  flex: 1,
  textAlign: 'center',
  fontSize: '11px',
  fontWeight: 500,
  textDecoration: 'none',
});

export const WeekRow = styled('div', {
  display: 'flex',
  gap: '2px',
  height: '54px',
});

export const DayButton = styled('button', {
  ...buttonReset,
  position: 'relative',
  flex: 1,
  minWidth: 0,
  height: '54px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // The circle, band and dot are absolutely placed layers (spec §7).
  '& [data-layer="band"]': {
    position: 'absolute',
    top: '3px',
    height: '48px',
    left: '-1px',
    right: '-1px',
    background: C.lightGreen,
  },
  // RS3: 48px, or the cell's width less 4px when the cell is narrower
  // (phones), so neighbours never touch and today's ring is never clipped.
  // Centred in the 54px cell either way (top 3px at full size).
  '& [data-layer="circle"]': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 'min(48px, calc(100% - 4px))',
    aspectRatio: '1 / 1',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
  },
  '& [data-layer="number"]': {
    position: 'relative',
    fontSize: '16px',
    fontWeight: 400,
  },
  '& [data-layer="dot"]': {
    position: 'absolute',
    top: '39px',
    left: '50%',
    width: '8px',
    height: '8px',
    marginLeft: '-4px',
    borderRadius: '50%',
    background: C.green,
  },
  // R15 / D13: a future block still holds a deactivated manager.
  '& [data-layer="dot"][data-flag="true"]': {
    background: C.amber,
    boxShadow: '0 0 0 1.5px #fff',
  },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible [data-layer="circle"]': focusRing,
  '&:disabled': { cursor: 'default' },
  variants: {
    look: {
      none: {},
      today: {
        '& [data-layer="circle"]': {
          boxShadow: `inset 0 0 0 1.5px ${C.green}`,
        },
        '& [data-layer="number"]': { color: C.darkGreen, fontWeight: 700 },
      },
      single: {
        '& [data-layer="circle"]': { background: C.green },
        '& [data-layer="number"]': { color: '#fff', fontWeight: 700 },
        '& [data-layer="dot"]:not([data-flag="true"])': { background: '#fff' },
      },
      // Repeat modal: a single picked date is a light circle.
      singleLight: {
        '& [data-layer="circle"]': { background: C.lightGreen },
        '& [data-layer="number"]': { color: C.darkGreen, fontWeight: 700 },
      },
      edge: {
        '& [data-layer="circle"]': {
          background: C.green,
          borderRadius: '14px',
        },
        '& [data-layer="number"]': { color: '#fff', fontWeight: 700 },
        '& [data-layer="dot"]:not([data-flag="true"])': { background: '#fff' },
      },
      middle: {},
    },
    band: {
      none: {},
      full: {},
      right: { '& [data-layer="band"]': { left: '50%' } },
      left: { '& [data-layer="band"]': { right: '50%' } },
    },
    outside: {
      true: { '& [data-layer="number"]': { color: C.disabled } },
    },
    blocked: {
      true: { '& [data-layer="number"]': { color: C.disabled } },
    },
  },
  defaultVariants: { look: 'none', band: 'none' },
});

// The calendar + panel row. On the page the calendar caps at 580px, not the
// prototype's 600: the app's content column is 36px narrower, and at 1440
// the panel needs about 470px for Cancel, Add another and Save on one row.
// Narrower than 836px the panel wraps under the calendar, full width.
export const Body = styled('div', {
  display: 'flex',
  alignItems: 'stretch',
  flexWrap: 'wrap',
  gap: '16px',
  // RS2: the 580px cap only while the panel sits beside the calendar
  // (480 + 16 + 340 = 836px of row). Stacked, the calendar takes the full
  // width: below 836px the second term is huge, so the cap lifts.
  [`& > ${CalendarRoot}`]: {
    maxWidth: 'max(580px, calc((836px - 100%) * 9999))',
  },
});

// ---- right panel (§8) -------------------------------------------------------

export const Panel = styled('div', {
  flex: '1 1 340px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '16px 24px',
  border: `1px solid ${C.border}`,
  '@phoneOnly': { padding: '16px' },
});

export const PanelHeading = styled('h3', {
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 1.4,
  // A single date never wraps; a range heading may, on narrow panels.
  whiteSpace: 'nowrap',
  // Takes focus by script after a delete (tabIndex -1); no ring for that.
  '&:focus:not(:focus-visible)': { outline: 'none' },
  variants: { range: { true: { whiteSpace: 'normal' } } },
});

// ---- toast (§10) ------------------------------------------------------------

export const ToastBox = styled('div', {
  position: 'fixed',
  top: '28px',
  right: '24px',
  // Above the app header and any modal overlay ($overlay is 10000).
  zIndex: 10001,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  minWidth: '268px',
  // Short toasts sit on one line (spec §10); a repeat that lists skips
  // wraps instead of running across the page.
  maxWidth: 'min(560px, calc(100vw - 32px))',
  // Title + body (two 19.6px lines) + 2px border = 56px, the prototype's
  // fixed height, without clipping a longer body: it grows instead.
  minHeight: '56px',
  padding: '7.4px 20px',
  borderRadius: '12px',
  background: '#fff',
  border: `1px solid ${C.softBorder}`,
  boxShadow: '0 0 6px 0 rgba(0,0,0,0.16)',
  color: C.ink,
  fontFamily: FONT,
  fontSize: '14px',
  lineHeight: 1.4,
  '@phoneOnly': { left: '16px', right: '16px', minWidth: 0 },
});

export const ToastText = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  paddingRight: '24px',
  '& strong': { fontWeight: 600 },
  '& span': { color: C.muted },
});

// ---- dialogs: Repeat (§11), the roster and the delete confirm ---

/** The prototype's near-black scrim, for the Operations dialogs only. */
export const DIALOG_SCRIM = 'rgba(0,0,0,0.7)';

/** UI spec §11: the dialog box (Repeat, roster, delete confirm). */
export const DialogBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxSizing: 'border-box',
  width: '648px',
  maxWidth: '100%',
  // Centred in the overlay's flex box; `auto` margins never push the top
  // out of reach when the dialog is taller than the viewport.
  margin: 'auto',
  paddingBottom: '24px',
  color: C.ink,
  fontFamily: FONT,
  fontSize: '16px',
  lineHeight: 1.4,
  textAlign: 'left',
  background: 'rgb(254,254,254)',
  borderRadius: '12px',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  '&:focus': { outline: 'none' },
  '& *': { boxSizing: 'border-box' },
});

export const DialogHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  minHeight: '57px',
  padding: '16px 24px',
});

export const DialogTitle = styled('h2', {
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 1.4,
  '@smallDesktop': { whiteSpace: 'nowrap' },
});

export const DialogContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '0 24px',
  '@phoneOnly': { padding: '0 16px' },
});

/** A confirm's message: 16/400 ink. */
export const DialogMessage = styled('p', {
  margin: 0,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 1.4,
});

export const DialogFooter = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: '16px',
  padding: '24px 24px 0',
  '@phoneOnly': { padding: '24px 16px 0' },
});

// ---- roster (not designed; Operations tokens) --------------------------

export const Toolbar = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px 16px',
});

export const RosterList = styled('ul', {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
});

export const RosterRow = styled('li', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 0',
  borderTop: `1px solid ${C.softBorder}`,
  '&:first-child': { borderTop: 'none' },
});

export const RosterRowMain = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px 16px',
});

export const RosterName = styled('p', {
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  fontSize: '16px',
  fontWeight: 600,
});

export const RosterDetail = styled('p', {
  margin: '2px 0 0',
  fontSize: '14px',
  color: C.muted,
  overflowWrap: 'anywhere',
});

export const Badge = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  height: '22px',
  padding: '0 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  variants: {
    tone: {
      // Both badges carry the same 1px inset border, in their tone.
      active: {
        background: C.lightGreen,
        color: C.darkGreen,
        boxShadow: 'inset 0 0 0 1px rgb(130,205,122)',
      },
      inactive: {
        background: 'rgb(253,243,224)',
        color: 'rgb(138,92,0)',
        boxShadow: `inset 0 0 0 1px ${C.amber}`,
      },
    },
  },
});

export const RowActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px 16px',
  '& button:disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

export const DangerLink = styled('button', {
  ...buttonReset,
  fontSize: '14px',
  fontWeight: 600,
  color: C.red,
  '&:hover:not(:disabled)': { textDecoration: 'underline' },
});

export const RosterForm = styled('form', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  borderRadius: '12px',
  boxShadow: `inset 0 0 0 1px ${C.border}`,
  background: '#fff',
});

export const FieldGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px 24px',
});

export const TextField = styled('label', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '14px',
  fontWeight: 600,
  '& input': {
    height: '36px',
    padding: '0 16px',
    border: 'none',
    borderRadius: '12px',
    background: '#fff',
    boxShadow: `inset 0 0 0 1px ${C.border}`,
    color: C.ink,
    font: 'inherit',
    fontWeight: 400,
    outline: 'none',
  },
  '& input::placeholder': { color: C.grey },
  '& input:focus': { boxShadow: `inset 0 0 0 1px ${C.green}` },
  '& input:disabled': { opacity: 0.6 },
});

export const FormButtons = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px 16px',
});
